import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface ClassifyButtonProps {
  auditId: string;
  onChange?: () => void;
  force?: boolean;
  label?: string;
  loadingLabel?: string;
  variant?: "default" | "outline";
}

type ClassifyResponse = { classified: number; flaggedCount?: number };

export function ClassifyButton({
  auditId,
  onChange,
  force = false,
  label = "Classify new reviews",
  loadingLabel = "Classifying…",
  variant = "outline",
}: ClassifyButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const handleClick = async () => {
    if (force && !window.confirm("Re-run AI on all reviews? This overwrites existing AI verdicts but preserves any operator overrides you've applied since.")) {
      return;
    }
    setIsLoading(true);
    setError(null);
    setStatus(null);

    const res = await api<ClassifyResponse>(
      `/admin/audits/${auditId}/classify${force ? "?force=true" : ""}`,
      { method: "POST" }
    );

    setIsLoading(false);

    if (!res.ok) {
      setError(res.error ?? `Failed (${res.status})`);
      return;
    }

    const n = res.data?.classified ?? 0;
    setStatus(
      n === 0
        ? "No new reviews to classify."
        : `Classified ${n} review${n === 1 ? "" : "s"}.`
    );
    setTimeout(() => setStatus(null), 4000);
    onChange?.();
  };

  return (
    <div>
      <Button variant={variant} onClick={handleClick} disabled={isLoading}>
        {isLoading && <Loader2 className="animate-spin" />}
        {isLoading ? loadingLabel : label}
      </Button>
      {error && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {status && (
        <p className="mt-2 text-sm text-muted-foreground" role="status">
          {status}
        </p>
      )}
    </div>
  );
}
